import React, { FunctionComponent, h } from "preact"; // eslint-disable-line @typescript-eslint/no-unused-vars
import { useEffect, useState } from "preact/hooks";
import dayjs, { ConfigType, UnitType } from "dayjs";

import cx from "classnames";

interface IAppProps {
  investmentId: string;
  publicationDate: string;
  includeEsg: string;
  widgetType: string;
  instanceId: string;
}

enum ApiSolutionType {
  MULTI = "multiAsset",
  MANAGED = "managed",
  DISCRETIONARY = "discretionary",
}

enum ApiInvestmentLinkType {
  STANDARD = "standard", // opens the investment detail page
  URL = "url", // external link
  NONE = "none", // disabled / no link
}

interface IApiInvestmentLink {
  name?: string;
  linkUrl?: string;
  description?: string;
}

interface IApiInvestment {
  id: string;
  name?: string;
  isin?: string;
  objective?: string;
  holdings?: number;
  inceptionDate?: string; // date-time
  managers?: string;
  volatilityTargetLow?: number;
  volatilityTargetHigh?: number;
  performanceComparator?: string;
  solutionType?: ApiSolutionType;
  links?: IApiInvestmentLink[];
  factsheetPublicationDate?: string; // date-time
  includeEsgWidget?: boolean;
  factsetId?: string;
  linkType?: ApiInvestmentLinkType;
  linkUrl?: string;
}

const App: FunctionComponent<IAppProps> = ({ investmentId, publicationDate, includeEsg, widgetType, instanceId }) => {
  const [investment, setInvestment] = useState<IApiInvestment>();
  const [allInvestments, setAllInvestments] = useState<any>([]);
  const [firstRun, setFirstRun] = useState<boolean>(false);
  const id = investmentId + widgetType + instanceId;

  const formatDate = (date: ConfigType, format = "YYYY-MM-DD") => dayjs(date).format(format);

  useEffect(() => {
    const fds = new globalThis.FdsComponents();
    fds.init("#" + id.toString());
    setFirstRun(true);
  }, []);

  useEffect(() => {
    const inv = allInvestments.find((x) => x.factsetId == investmentId);
    setInvestment(inv as IApiInvestment);
  }, [allInvestments]);

  const date = formatDate(publicationDate || undefined);

  return (
    <div
      class="fds-aware"
      id={id}
    >
      {(widgetType == "all" || widgetType == "investmentDetails") && (
        <section className="factset-widget-section">
          <fds-investment-details
            id={investmentId}
            date={date}
          />
        </section>
      )}
      {(widgetType == "all" || widgetType == "discreteAnnualPerformance") && (
        <section className="factset-widget-section">
          <fds-discrete-calendar-performance
            id={investmentId}
            date={date}
          />
        </section>
      )}
      {(widgetType == "all" || widgetType == "cumulativeAnnualPerformance") && (
        <section className="factset-widget-section">
          <fds-cumulative-performance
            id={investmentId}
            date={date}
          />
        </section>
      )}
      {widgetType == "cumulativeAnnualPerformanceChart" && (
        <section className="factset-widget-section">
          <fds-cumulative-performance
            id={investmentId}
            date={date}
            display-table="0"
            display-caveat="0"
          />
        </section>
      )}
      {widgetType == "cumulativeAnnualPerformanceTable" && (
        <section className="factset-widget-section">
          <fds-cumulative-performance
            id={investmentId}
            date={date}
            display-chart="0"
            display-caveat="0"
          />
        </section>
      )}
      {widgetType == "cumulativeAnnualPerformanceCaveat" && (
        <section className="factset-widget-section">
          <fds-cumulative-performance
            id={investmentId}
            date={date}
            display-chart="0"
            display-table="0"
          />
        </section>
      )}
      {(widgetType == "all" || widgetType == "assetClassBreakdown") && (
        <section className="factset-widget-section">
          <fds-asset-classes
            id={investmentId}
            date={date}
          />
          {includeEsg && (
            <section className="factset-widget-section">
              <fds-esg-bundle
                id={investmentId}
                date={date}
              />
            </section>
          )}
        </section>
      )}
    </div>
  );
};

export default App;
